import type { SDKModule, SDKContext, PreviewActionDetail } from '../types';
import {
  closestFrom,
  findFormWrapperByName,
  getActionHref,
  getActionTarget,
  isNativeLinkTag,
} from '../utils/dom';
import { log } from '../utils/logger';

/**
 * Click Action Module — sole runtime owner of editor-configured click actions.
 *
 * Attribute contract (dual-track):
 *   - `data-pt-href` (+ `data-pt-target`)  → current contract, valid on ANY tag.
 *     On `<a>` it carries non-navigation actions (`form:` / `javascript:void(0)`)
 *     that a native href cannot express.
 *   - bare `href` where the platform has no href semantics (div / span / img /
 *     button / h1 … — see utils/dom.ts) → legacy contract (pages published before
 *     the migration). Kept forever: the SDK is loaded from a shared URL, so every
 *     historical page runs this code. An SVG `<use href="#icon">` is NOT this:
 *     that href belongs to the platform, and clicks on it walk out to the CTA.
 *   - plain `<a href>` (no data-pt-href)   → native, the SDK never touches it.
 *     This also covers the publish-generated hidden SEO anchors nested inside
 *     `[data-pt-href]` hosts: closest() resolves to the anchor first and we bail.
 *
 * Action dispatch (same prefix protocol on both tracks):
 *   - form:formName     → trigger form submission
 *   - javascript:void(0) + data-pt-onclick → execute custom JS (skipped in preview)
 *   - tel: / mailto: / #anchor / URL       → simulate <a> behavior, including
 *     Ctrl/Cmd/Shift-click and middle-click opening a new tab like a real link.
 *
 * Preview mode suppresses side effects that are *not* reversible from the viewer's
 * seat — analytics, webhooks, real form POSTs, arbitrary JS. **Navigation is not one
 * of them**: it is the whole point of the action and the only way an editor can check
 * "did I wire this to the right place". Skipping it left non-<a> actions looking dead
 * in preview while <a> tags (browser-native) and legacy editor-injected runtimes kept
 * navigating — three generations of the editor behaving differently on the same page.
 * The pt-preview-action event is still dispatched so hosts can observe the intent.
 */
export function createClickAction(): SDKModule {
  let ctx: SDKContext;
  let clickHandler: ((e: Event) => void) | null = null;
  let auxHandler: ((e: Event) => void) | null = null;
  let downHandler: ((e: Event) => void) | null = null;

  const OWNER_SELECTOR = '[data-pt-href], [href]';

  /**
   * Nearest element that actually owns the click; null when the browser owns it.
   *
   * The selector can match an element whose `href` is not an action at all — an SVG
   * `<use href="#icon">` inside a CTA is the common one (getActionHref returns null
   * for it, see utils/dom.ts). Keep walking outward so the click still reaches the
   * CTA that wraps the icon, but stop at a native link: the browser is already
   * navigating that one, and looking past it would let the publish-generated hidden
   * SEO anchor be navigated by the browser *and* opened in a tab by us.
   */
  function resolveOwner(target: EventTarget | null): { el: Element; href: string } | null {
    let el = closestFrom(target, OWNER_SELECTOR);
    while (el) {
      const href = getActionHref(el);
      if (href) return { el, href };
      if (isNativeLinkTag(el)) return null;
      el = el.parentElement ? closestFrom(el.parentElement, OWNER_SELECTOR) : null;
    }
    return null;
  }

  /**
   * Navigation actions only. `form:` / custom-JS actions have no new-tab semantics,
   * so middle-click must leave them to the browser — including the autoscroll it
   * would otherwise start (handleDown and handleAux must agree on this set, or the
   * autoscroll gets suppressed for a gesture that then does nothing at all).
   */
  function isNavigable(el: Element, href: string): boolean {
    return (
      !href.startsWith('form:') &&
      href !== 'javascript:void(0)' &&
      !el.hasAttribute('data-pt-onclick')
    );
  }

  function dispatchPreview(detail: PreviewActionDetail): void {
    try {
      document.dispatchEvent(new CustomEvent('pt-preview-action', { detail }));
    } catch (e) {
      log('warn', 'dispatch preview action failed', e);
    }
  }

  function navigate(el: Element, href: string, e: MouseEvent, forceNewTab: boolean): void {
    const openTarget = getActionTarget(el);
    const newTab = forceNewTab || openTarget === '_blank' || e.ctrlKey || e.metaKey || e.shiftKey;

    // Preview still announces the intent (hosts may render an overlay), but the
    // navigation itself goes through — see the module header for why.
    if (ctx.preview) {
      dispatchPreview({
        kind: newTab ? 'open' : 'navigate',
        reason: 'preview: navigating',
        payload: { href, target: newTab ? '_blank' : '_self' },
      });
      log('info', 'preview: navigating to ' + href);
    }

    if (newTab) {
      window.open(href, '_blank');
    } else {
      window.location.href = href;
    }
  }

  function handleClick(e: Event): void {
    const owner = resolveOwner(e.target);
    if (!owner) return;
    // No destructuring anywhere in src/: esbuild refuses to lower it for the
    // safari14 build target (Safari 14 destructuring bug).
    const el = owner.el;
    const href = owner.href;

    // ── form:formName → trigger form submission (works in preview too) ──
    if (href.startsWith('form:')) {
      e.preventDefault();
      const formName = href.slice(5);
      const formWrap = findFormWrapperByName(formName);
      if (formWrap) {
        const form = formWrap.querySelector('form');
        if (form) {
          if (typeof form.requestSubmit === 'function') {
            form.requestSubmit();
          } else {
            form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
          }
        } else {
          log('warn', 'click-action form: no <form> inside wrapper "' + formName + '"');
        }
      } else {
        log('warn', 'click-action form: no wrapper named "' + formName + '"');
      }
      return;
    }

    // ── javascript:void(0) + data-pt-onclick → execute custom JS ──
    if (el.hasAttribute('data-pt-onclick')) {
      e.preventDefault();
      const code = el.getAttribute('data-pt-onclick') || '';
      if (ctx.preview) {
        dispatchPreview({ kind: 'onclick', reason: 'preview: skipped data-pt-onclick', payload: code });
        log('info', 'preview: skipped data-pt-onclick', code);
        return;
      }
      try {
        // eslint-disable-next-line no-new-func
        new Function(code)();
      } catch (err) {
        log('error', 'data-pt-onclick threw', err);
      }
      return;
    }
    if (href === 'javascript:void(0)') return;

    // ── tel: / mailto: / #anchor / URL → simulate <a> behavior ──
    e.preventDefault();
    navigate(el, href, e as MouseEvent, false);
  }

  /** Middle-click on a navigable action opens a new tab, like a real link. */
  function handleAux(e: Event): void {
    if ((e as MouseEvent).button !== 1) return;
    const owner = resolveOwner(e.target);
    if (!owner) return;
    const el = owner.el;
    const href = owner.href;
    if (!isNavigable(el, href)) return;
    e.preventDefault();
    navigate(el, href, e as MouseEvent, true);
  }

  /** Suppress the middle-button autoscroll that real links also suppress. */
  function handleDown(e: Event): void {
    if ((e as MouseEvent).button !== 1) return;
    const owner = resolveOwner(e.target);
    if (!owner || !isNavigable(owner.el, owner.href)) return;
    e.preventDefault();
  }

  return {
    name: 'click-action',
    init(context: SDKContext): void {
      ctx = context;
      clickHandler = handleClick;
      auxHandler = handleAux;
      downHandler = handleDown;
      // Bubble phase — runs after event-tracker (which uses capture)
      document.addEventListener('click', clickHandler, false);
      document.addEventListener('auxclick', auxHandler, false);
      document.addEventListener('mousedown', downHandler, false);
    },
    destroy(): void {
      if (clickHandler) {
        document.removeEventListener('click', clickHandler, false);
        clickHandler = null;
      }
      if (auxHandler) {
        document.removeEventListener('auxclick', auxHandler, false);
        auxHandler = null;
      }
      if (downHandler) {
        document.removeEventListener('mousedown', downHandler, false);
        downHandler = null;
      }
    },
  };
}
